/**
 * presenterStore.ts
 * State for the Presenter Overlay toolbar tools and whiteboard.
 */

import { create } from 'zustand';

export type DrawingTool = 'pen' | 'highlighter' | 'laser' | 'eraser' | 'none';

export interface StrokeStyle {
  color: string;
  width: number;
  opacity: number;
}

interface PresenterState {
  // Active drawing tool
  activeTool: DrawingTool;
  setActiveTool: (tool: DrawingTool) => void;

  // Pen settings
  penStyle: StrokeStyle;
  setPenStyle: (style: Partial<StrokeStyle>) => void;

  // Highlighter settings
  highlighterStyle: StrokeStyle;
  setHighlighterStyle: (style: Partial<StrokeStyle>) => void;

  // Laser pointer position (null when not active)
  laserPosition: { x: number; y: number } | null;
  setLaserPosition: (pos: { x: number; y: number } | null) => void;

  // Whiteboard panel visibility
  whiteboardOpen: boolean;
  setWhiteboardOpen: (open: boolean) => void;

  // Embedded browser URL
  browserUrl: string;
  setBrowserUrl: (url: string) => void;
  browserOpen: boolean;
  setBrowserOpen: (open: boolean) => void;

  // Name picker panel
  namePickerOpen: boolean;
  setNamePickerOpen: (open: boolean) => void;

  // Results panel visibility
  resultsOpen: boolean;
  setResultsOpen: (open: boolean) => void;

  // Canvas undo stack (serialized strokes as data URLs)
  canvasHistory: string[];
  pushCanvasSnapshot: (dataUrl: string) => void;
  undoCanvas: () => string | null;
  clearCanvas: () => void;
}

export const usePresenterStore = create<PresenterState>((set, get) => ({
  activeTool: 'none',
  setActiveTool: (tool) => set({ activeTool: tool }),

  penStyle: { color: '#EF4444', width: 4, opacity: 1.0 },
  setPenStyle: (style) =>
    set((s) => ({ penStyle: { ...s.penStyle, ...style } })),

  highlighterStyle: { color: '#FACC15', width: 24, opacity: 0.35 },
  setHighlighterStyle: (style) =>
    set((s) => ({ highlighterStyle: { ...s.highlighterStyle, ...style } })),

  laserPosition: null,
  setLaserPosition: (pos) => set({ laserPosition: pos }),

  whiteboardOpen: false,
  setWhiteboardOpen: (open) => set({ whiteboardOpen: open }),

  browserUrl: '',
  setBrowserUrl: (url) => set({ browserUrl: url }),
  browserOpen: false,
  setBrowserOpen: (open) => set({ browserOpen: open }),

  namePickerOpen: false,
  setNamePickerOpen: (open) => set({ namePickerOpen: open }),

  resultsOpen: false,
  setResultsOpen: (open) => set({ resultsOpen: open }),

  canvasHistory: [],
  pushCanvasSnapshot: (dataUrl) =>
    set((s) => ({
      canvasHistory: [...s.canvasHistory, dataUrl].slice(-20), // keep last 20 states
    })),
  undoCanvas: () => {
    const { canvasHistory } = get();
    if (canvasHistory.length < 2) return null;
    const newHistory = canvasHistory.slice(0, -1);
    set({ canvasHistory: newHistory });
    return newHistory[newHistory.length - 1];
  },
  clearCanvas: () => set({ canvasHistory: [] }),
}));
